import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { trackEvent } from '../../analytics';
import { BottomSheet } from './BottomSheet';
import { apiFetch, getAccessTokenOrNull } from '../../utils/api';
import { getTelegramId } from '../../utils/telegramUser';
import './PostCheckoutFeedbackSheet.css';

export type FeedbackContext = {
  provider?: string;
  planId?: number;
  orderId?: string;
};

type AnswerKey = 'too_expensive' | 'payment_failed' | 'no_trust' | 'will_pay_later' | 'changed_mind' | 'wrong_amount' | 'other';

export function PostCheckoutFeedbackSheet(props: {
  open: boolean;
  onClose: () => void;
  context?: FeedbackContext;
}) {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);

  const [selected, setSelected] = useState<AnswerKey | null>(null);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  // Reset state on each new open so the form is fresh
  useEffect(() => {
    if (props.open) {
      setSelected(null);
      setOtherText('');
      setSubmitting(false);
      setSubmitted(false);
      setDontAskAgain(false);
    }
  }, [props.open]);

  const handleSelect = useCallback((key: AnswerKey) => {
    setSelected(key);
  }, []);

  const handleClose = useCallback(() => {
    // If "don't ask again" is checked, persist even without submit
    if (dontAskAgain) {
      try { localStorage.setItem('checkoutFeedbackOptOut', '1'); } catch {}
    }
    // Always set cooldown so modal doesn't re-appear immediately
    try { localStorage.setItem('lastCheckoutFeedback', String(Date.now())); } catch {}
    props.onClose();
  }, [dontAskAgain, props]);

  const handleSubmit = useCallback(async () => {
    if (!selected) return;
    if (submitting) return;

    setSubmitting(true);
    try {
      const token = await getAccessTokenOrNull(services);
      const telegramId = getTelegramId();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await apiFetch('/api/checkout-feedback', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          answer_key: selected,
          answer_text: otherText.trim() || null,
          provider: props.context?.provider || null,
          plan_id: props.context?.planId || null,
          order_id: props.context?.orderId || null,
          telegram_id: telegramId || null,
        }),
      });

      // Track analytics event
      try {
        void trackEvent('post_checkout_feedback', {
          origin: 'premium',
          metadata: {
            answer_key: selected,
            has_text: otherText.trim().length > 0,
            provider: props.context?.provider || null,
            dont_ask_again: dontAskAgain,
          },
        });
      } catch {}

      // Save cooldown timestamp
      try {
        localStorage.setItem('lastCheckoutFeedback', String(Date.now()));
      } catch {}

      // Save "don't ask again" opt-out
      if (dontAskAgain) {
        try { localStorage.setItem('checkoutFeedbackOptOut', '1'); } catch {}
      }

      setSubmitted(true);

      // Close after showing thank-you
      setTimeout(() => {
        props.onClose();
      }, 1500);
    } catch (e) {
      console.error('[checkout-feedback] submit error:', e);
    } finally {
      setSubmitting(false);
    }
  }, [selected, otherText, submitting, services, props, dontAskAgain]);

  // Early return AFTER all hooks — hooks must always run in the same order
  if (!props.open) return null;

  if (submitted) {
    return (
      <BottomSheet open={props.open} onClose={handleClose} ariaLabel="Feedback">
        <div className="pcf-sheet">
          <div className="pcf-thankyou">
            <span className="pcf-thankyou-icon">✓</span>
            <p>{t('post_checkout.thank_you')}</p>
          </div>
        </div>
      </BottomSheet>
    );
  }

  const options: { key: AnswerKey; labelKey: string; emoji: string }[] = [
    { key: 'too_expensive', labelKey: 'post_checkout.opt_too_expensive', emoji: '💰' },
    { key: 'payment_failed', labelKey: 'post_checkout.opt_payment_failed', emoji: '💳' },
    { key: 'no_trust', labelKey: 'post_checkout.opt_no_trust', emoji: '🔒' },
    { key: 'will_pay_later', labelKey: 'post_checkout.opt_will_pay_later', emoji: '⏰' },
    { key: 'changed_mind', labelKey: 'post_checkout.opt_changed_mind', emoji: '🤔' },
    { key: 'wrong_amount', labelKey: 'post_checkout.opt_wrong_amount', emoji: '🔢' },
    { key: 'other', labelKey: 'post_checkout.opt_other', emoji: '💬' },
  ];

  return (
    <BottomSheet open={props.open} onClose={handleClose} ariaLabel="Post-checkout feedback">
      <div className="pcf-sheet">
        <h3 className="pcf-title">{t('post_checkout.title')}</h3>

        <div className="pcf-options">
          {options.map((opt) => (
            <button
              key={opt.key}
              className={`pcf-option ${selected === opt.key ? 'pcf-option-selected' : ''}`}
              onClick={() => handleSelect(opt.key)}
              type="button"
            >
              <span className="pcf-option-emoji">{opt.emoji}</span>
              <span className="pcf-option-label">{t(opt.labelKey)}</span>
            </button>
          ))}
        </div>

        {selected === 'other' && (
          <textarea
            className="pcf-textarea"
            placeholder={t('post_checkout.other_placeholder')}
            value={otherText}
            onInput={(e) => setOtherText((e.target as HTMLTextAreaElement).value)}
            maxLength={1000}
            rows={3}
          />
        )}

        <label className="pcf-dont-ask">
          <input
            type="checkbox"
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain((e.target as HTMLInputElement).checked)}
          />
          <span>{t('post_checkout.dont_ask_again')}</span>
        </label>

        <button
          className="pcf-submit"
          onClick={handleSubmit}
          disabled={!selected || submitting}
          type="button"
        >
          {submitting ? '...' : t('post_checkout.btn_submit')}
        </button>
      </div>
    </BottomSheet>
  );
}
